import {Flex, Image, keyframes, Skeleton, Text} from '@chakra-ui/react'
import {useDispatch, useSelector} from 'react-redux'
import VolumeIcon from '@assets/svgs/volume.svg'
import {useEffect, useState} from 'react'
import {
  updateAudio,
  updateIsDisableBtn,
} from '@redux/learn-part-three/learnPartThree'
import learnPartThreeRequest from '@apis/learn-part-three'
import Loading from '@components/Loading'
import {styleByTextTag} from '@utils'
import {CommonColors} from '@utils/CommonStyles'

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const PracticeIdea = () => {
  const {currentContent, practiceData, numberRecorded, sessionId, isRecording} =
    useSelector((state: any) => state.learnPartThree)
  const [practiceIdeas, setPracticeIdeas] = useState<any>()
  const [isPracticed, setIsPracticed] = useState(false)
  const [textNoTag, setTextNoTag] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    getPracticeIdeas()
  }, [])

  useEffect(() => {
    if (!practiceIdeas) return
    setTextNoTag(practiceIdeas?.text?.replace(/<[^>]*>?/g, ''))

    const primaryTextElements = document.querySelectorAll(
      '.practice-idea-container .primary-text',
    )

    primaryTextElements.forEach((element: Element) => {
      ;(element as HTMLElement).style.cursor = 'pointer'
      const tooltip = document.createElement('div')
      tooltip.className = 'tooltip'
      tooltip.textContent = currentContent?.data?.popup
      tooltip.style.cssText = `
        position: absolute;
        background: #333;
        color: white;
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 14px;
        display: none;
        z-index: 1000;
      `

      element.addEventListener('mouseenter', e => {
        const rect = (e.target as HTMLElement).getBoundingClientRect()
        tooltip.style.left = `${rect.left}px`
        tooltip.style.top = `${rect.bottom + 5}px`
        tooltip.style.display = 'block'
      })

      element.addEventListener('mouseleave', () => {
        tooltip.style.display = 'none'
      })

      document.body.appendChild(tooltip)
    })
  }, [practiceIdeas])

  useEffect(() => {
    if (practiceData) {
      setIsPracticed(true)
    }
  }, [practiceData])

  useEffect(() => {
    if (numberRecorded > 1) {
      dispatch(updateIsDisableBtn(false))
    } else {
      dispatch(updateIsDisableBtn(true))
    }
  }, [numberRecorded])

  useEffect(() => {
    if (!practiceData) return
    const accuracy = calculateAccuracy()

    if (accuracy > 50) {
      dispatch(updateIsDisableBtn(false))
    }
  }, [practiceData])

  const getPracticeIdeas = async () => {
    setIsLoading(true)

    try {
      const params = {
        session_id: sessionId,
        step: currentContent?.step,
      }

      const res = await learnPartThreeRequest.getPracticeIdeas(params)
      setPracticeIdeas(res?.data?.data)
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }

  const compareWords = (word: string) => {
    const correctWords = practiceData?.result?.text?.split(' ') || []
    return correctWords.includes(word)
  }

  const calculateAccuracy = () => {
    if (!textNoTag || !practiceData?.result?.text) return 0

    const userWords = textNoTag.split(' ')
    const correctCount = userWords.filter(word => compareWords(word)).length
    return (correctCount / userWords.length) * 100
  }

  const handlePlayAudio = () => {
    if (isRecording) return
    const audioData = {
      time: Date.now(),
      url: practiceIdeas?.audio,
    }
    dispatch(updateAudio(audioData))
  }

  return (
    <Flex
      className="practice-idea-container"
      alignItems={'center'}
      flexDirection={'column'}
      gap={'24px'}>
      {isLoading ? (
        <Skeleton h={'24px'} w={'100%'} maxW={'300px'} />
      ) : (
        <Text color={'#667085'} fontSize={'18px'} textTransform={'uppercase'}>
          {currentContent?.data?.title}
        </Text>
      )}

      <Flex
        gap={'16px'}
        maxWidth={'650px'}
        animation={`${slideUp} 0.5s ease-out forwards`}>
        <Image
          src={VolumeIcon}
          alt="volume"
          width={'56px'}
          height={'56px'}
          cursor={'pointer'}
          onClick={handlePlayAudio}
        />
        {isLoading ? (
          <Flex direction="column" gap="8px" w="300px">
            <Skeleton h="24px" w="100%" />
            <Skeleton h="24px" w="80%" />
            <Skeleton h="24px" w="90%" />
          </Flex>
        ) : (
          <Text fontSize={'20px'} color={'#475467'}>
            {isPracticed
              ? textNoTag?.split(' ').map((word, index) => (
                  <Text
                    key={index}
                    as="span"
                    textDecoration="underline"
                    style={{
                      color: compareWords(word) ? CommonColors.primary : 'red',
                    }}>
                    {word}{' '}
                  </Text>
                ))
              : practiceIdeas?.text && styleByTextTag(practiceIdeas.text)}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

export default PracticeIdea
